/**
 * Tiny synchronous key/value persistence seam for JSON values.
 *
 * Synchronous on purpose: callers like the anonymous feed engine persist inside synchronous
 * mutations (markPostRead → broadcast → persist). Tests can inject a fake store.
 *
 * A user's store has two backends:
 * - 'local': localStorage (the default). Small and always available.
 * - 'idb': IndexedDB, used with offline reading, which stores far more than localStorage's ~5MB.
 *   IndexedDB is async, so the store keeps all of the user's entries in memory (as JSON strings):
 *   reads are served from memory, writes update memory and are written through in the background.
 *   On startup the entries are loaded once; `ready` resolves when that is done.
 * Which backend holds a user's data is remembered in localStorage, so it is known synchronously.
 */
export interface JsonStore {
  /** Parse the JSON stored at `key`, or return `fallback` if absent/corrupt. */
  getJson<T>(key: string, fallback: T): T;
  /** JSON-serialize `value` and store it at `key`. */
  setJson(key: string, value: unknown): void;
  /** Delete `key`. No-op if absent. */
  remove(key: string): void;
  /** Resolves once reads see the stored data. Immediate for localStorage; with IndexedDB it waits
   *  for the initial load. Reads before that return the fallback. Never rejects. */
  ready: Promise<void>;
}

export type JsonStoreBackend = 'local' | 'idb';

export interface UserJsonStore extends JsonStore {
  getBackend(): JsonStoreBackend;
  /**
   * Move all of the user's entries to `backend` and use it from now on. Takes effect immediately
   * (once `ready`); the returned promise resolves when the move is persisted. If IndexedDB is not
   * available, or the data doesn't fit into localStorage, the store stays where it is.
   */
  setBackend(backend: JsonStoreBackend): Promise<void>;
}

const DB_NAME = 'downread-json-store';
const DB_STORE = 'kv';
// Outside the user prefix on purpose, so moving the user's entries doesn't move the flag too.
const backendFlagKey = (userId: string) => `json-store-backend-${userId}`;

/** The anonymous store: localStorage, keys unprefixed. */
export const anonJsonStore: JsonStore = {
  getJson<T>(key: string, fallback: T): T {
    try {
      const s = localStorage.getItem(key);
      return s ? JSON.parse(s) as T : fallback;
    } catch (e) {
      console.error('json-store: failed to read', key, e);
      return fallback;
    }
  },

  setJson(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error('json-store: failed to persist', key, e);
    }
  },

  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('json-store: failed to remove', key, e);
    }
  },

  ready: Promise.resolve(),
};

const userStores = new Map<string, UserJsonStore>();

/**
 * The store of `userId`. Every key is stored as `<userId>-<key>`, so several accounts can use the
 * same device one after another without sharing data. One instance per user, so all callers see
 * the same backend.
 */
export function getJsonStore(userId: string): UserJsonStore {
  let store = userStores.get(userId);
  if (!store) userStores.set(userId, store = createUserStore(userId));
  return store;
}

/** Delete every user's IndexedDB entries and forget the store instances (logout). The caller
 *  wipes localStorage (including the backend flags) itself. */
export async function clearUserJsonStores(): Promise<void> {
  userStores.clear();
  await deleteDb();
}

/** Delete the IndexedDB database if no store uses it (every user's data is in localStorage), to
 *  give its disk space back. Records deleted by a move to localStorage keep occupying space until
 *  the browser compacts the files; deleting the database frees it right away. */
export async function deleteUnusedJsonStoreDatabase(): Promise<void> {
  if (Array.from(userStores.values()).some(store => store.getBackend() == 'idb')) return;
  for (let i = 0; i < localStorage.length; i++)
    if (localStorage.key(i)?.startsWith('json-store-backend-')) return;
  await deleteDb();
}

// --- IndexedDB helpers ---

let dbPromise: Promise<IDBDatabase> | undefined;

function openDb(): Promise<IDBDatabase> {
  return dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(DB_STORE);
    request.onsuccess = () => {
      const db = request.result;
      // Let another tab delete (or upgrade) the database; the next access reopens it.
      db.onversionchange = () => {
        db.close();
        dbPromise = undefined;
      };
      resolve(db);
    };
    request.onerror = () => reject(request.error);
  }).catch(e => {
    dbPromise = undefined; // let a later call retry
    throw e;
  });
}

async function deleteDb(): Promise<void> {
  if (typeof indexedDB == 'undefined') return;
  const open = dbPromise;
  dbPromise = undefined;
  try { (await open)?.close(); } catch { /* never opened */ }
  await new Promise<void>(resolve => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => {
      console.error('json-store: failed to delete the database', request.error);
      resolve();
    };
    request.onblocked = () => console.warn('json-store: deletion waiting for other tabs to close the database');
  });
}

function runTransaction(db: IDBDatabase, fn: (store: IDBObjectStore) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    fn(tx.objectStore(DB_STORE));
    tx.oncomplete = () => resolve();
    tx.onerror = tx.onabort = () => reject(tx.error);
  });
}

// Every key that starts with `prefix` ('￿' sorts after any character used in our keys).
const prefixRange = (prefix: string) => IDBKeyRange.bound(prefix, prefix + '￿');

function loadPrefix(db: IDBDatabase, prefix: string): Promise<Map<string, string>> {
  return new Promise((resolve, reject) => {
    const entries = new Map<string, string>();
    const request = db.transaction(DB_STORE, 'readonly').objectStore(DB_STORE).openCursor(prefixRange(prefix));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return resolve(entries);
      entries.set((cursor.key as string).substring(prefix.length), cursor.value as string);
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
  });
}

// --- user store ---

function createUserStore(userId: string): UserJsonStore {
  const prefix = `${userId}-`;
  let backend: JsonStoreBackend = readFlag(userId) == 'idb' && typeof indexedDB != 'undefined' ? 'idb' : 'local';
  // IndexedDB mode: the user's entries (unprefixed key → JSON string). Authoritative in memory.
  let memory = new Map<string, string>();
  let loaded = backend == 'local';
  // Keys written before the initial IndexedDB load finished; the load must not overwrite them.
  const writtenEarly = new Set<string>();
  // While moving local → idb, writes go to both, so a crash before the move is persisted loses nothing.
  let movingToIdb = false;

  const ready: Promise<void> = loaded ? Promise.resolve() : (async () => {
    try {
      const entries = await loadPrefix(await openDb(), prefix);
      entries.forEach((value, key) => { if (!writtenEarly.has(key)) memory.set(key, value); });
    } catch (e) {
      // Without IndexedDB the data there is unreachable; carry on with localStorage.
      console.error('json-store: IndexedDB load failed, falling back to localStorage', e);
      backend = 'local';
      memory = new Map();
    }
    writtenEarly.clear();
    loaded = true;
  })();

  const localRead = (key: string): string | null => {
    try { return localStorage.getItem(prefix + key); }
    catch (e) { console.error('json-store: failed to read', prefix + key, e); return null; }
  };
  const localWrite = (key: string, value: string | undefined) => {
    try {
      if (value == undefined) localStorage.removeItem(prefix + key);
      else localStorage.setItem(prefix + key, value);
    } catch (e) { console.error('json-store: failed to persist', prefix + key, e); }
  };
  const idbWrite = (key: string, value: string | undefined) =>
    openDb()
      .then(db => runTransaction(db, store => value == undefined ? store.delete(prefix + key) : store.put(value, prefix + key)))
      .catch(e => console.error('json-store: failed to persist to IndexedDB', prefix + key, e));

  const write = (key: string, value: string | undefined) => {
    if (backend == 'local') return localWrite(key, value);
    if (!loaded) writtenEarly.add(key);
    if (value == undefined) memory.delete(key);
    else memory.set(key, value);
    idbWrite(key, value);
    if (movingToIdb) localWrite(key, value);
  };

  // All of the user's localStorage entries (unprefixed key → JSON string).
  const localEntries = (): Map<string, string> => {
    const entries = new Map<string, string>();
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefix)) entries.set(k.substring(prefix.length), localStorage.getItem(k)!);
    }
    return entries;
  };

  async function moveToIdb(): Promise<void> {
    let db: IDBDatabase;
    try { db = await openDb(); }
    catch (e) { console.error('json-store: IndexedDB unavailable, staying in localStorage', e); return; }
    if (backend == 'idb') return; // switched meanwhile
    // Switch in memory right away; localStorage keeps getting the writes until IndexedDB has it all.
    memory = localEntries();
    backend = 'idb';
    movingToIdb = true;
    const snapshot = new Map(memory);
    try {
      await runTransaction(db, store => {
        store.delete(prefixRange(prefix)); // leftovers of an interrupted earlier move
        snapshot.forEach((value, key) => store.put(value, prefix + key));
      });
    } catch (e) {
      console.error('json-store: move to IndexedDB failed, staying in localStorage', e);
      movingToIdb = false;
      backend = 'local';
      memory = new Map();
      return;
    }
    if (!movingToIdb) return; // moved back to localStorage meanwhile
    movingToIdb = false;
    try { localStorage.setItem(backendFlagKey(userId), 'idb'); }
    catch (e) { console.error('json-store: failed to persist backend flag', e); return; }
    localEntries().forEach((_, key) => localStorage.removeItem(prefix + key));
  }

  async function moveToLocal(): Promise<void> {
    movingToIdb = false;
    const written: string[] = [];
    try {
      memory.forEach((value, key) => { localStorage.setItem(prefix + key, value); written.push(key); });
    } catch (e) {
      // Doesn't fit (quota): undo the partial copy and stay in IndexedDB.
      console.error('json-store: data does not fit into localStorage, staying in IndexedDB', e);
      written.forEach(key => localStorage.removeItem(prefix + key));
      return;
    }
    backend = 'local';
    memory = new Map();
    localStorage.removeItem(backendFlagKey(userId));
    try {
      await runTransaction(await openDb(), store => store.delete(prefixRange(prefix)));
    } catch (e) {
      console.error('json-store: failed to delete IndexedDB entries', e);
    }
  }

  return {
    getJson<T>(key: string, fallback: T): T {
      const s = backend == 'local' ? localRead(key) : memory.get(key);
      try {
        return s ? JSON.parse(s) as T : fallback;
      } catch (e) {
        console.error('json-store: failed to parse', prefix + key, e);
        return fallback;
      }
    },

    setJson(key: string, value: unknown): void {
      write(key, JSON.stringify(value));
    },

    remove(key: string): void {
      write(key, undefined);
    },

    ready,

    getBackend: () => backend,

    async setBackend(target: JsonStoreBackend): Promise<void> {
      if (!loaded) await ready;
      if (target == 'idb' && typeof indexedDB == 'undefined') return;
      if (target == backend) return;
      return target == 'idb' ? moveToIdb() : moveToLocal();
    },
  };
}

function readFlag(userId: string): string | null {
  try { return localStorage.getItem(backendFlagKey(userId)); }
  catch { return null; }
}
